import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, DropdownItem, DropdownMenu, Form, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, Dropdown, PinTypeIcon } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useGeneralInfo } from "modules/AppointmentDetails/components/Interventions/components/GeneralInfo/components/GeneralInfoForm/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";

import type { DropdownItemProps } from "components";
import type { Appointment, Intervention, PIN_TYPES } from "models";
import type { INTERVENTION_TABS } from "modules/AppointmentDetails/components/Interventions/components";

type GeneralInfoFormProps = {
  intervention: Intervention;
  appointment: Appointment | null;
  onPinTypeSet: (pinTypeID: PIN_TYPES | null) => void;
  pinTypeOptions: DropdownItemProps<PIN_TYPES>[];
  typeID: PIN_TYPES | null;
  handleTabSelection: (tab: INTERVENTION_TABS) => void;
  onSetHasChanges: (hasChanges: boolean) => void;
};

export type GeneralInfoFormValues = Pick<Intervention, "title" | "description" | "price" | "customer_ok">;

const FormHelper = createFormHelper<GeneralInfoFormValues>();

export const GeneralInfoForm = ({ intervention, appointment, onPinTypeSet, typeID, pinTypeOptions, onSetHasChanges, handleTabSelection }: GeneralInfoFormProps) => {
  const { t } = useTranslation();

  const {
    title,
    isDirty,
    isSubmitted,
    pinTypeID,
    followDMS,
    isDisabled,
    customerOk,
    openDropdown,
    isDeleteInterventionPending,
    canUpdateInterventions,
    cannotToggleCustomerOK,
    showDeleteInterventionModal,
    toggleDropdown,
    handlePinUpdate,
    toggleCustomerOk,
    handleButtonGroupClick,
    handleDeleteLocalIntervention,
    handleCancelDeleteLocalIntervention,
    handleDeleteLocalInterventionConfirm
  } = useGeneralInfo({
    intervention,
    typeID,
    appointment,
    onPinTypeSet,
    onSetHasChanges,
    handleTabSelection
  });

  const selectedLocation = useAppointmentLocation();

  return (
    <>
      <Form.Group className="input-row-equal-spacing">
        <Form.Field width={16} required error={isSubmitted && !title}>
          <label>{t("v8_title", "Title")}</label>
          <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_title", "Title"), fluid: true, disabled: isDisabled || followDMS }} />
        </Form.Field>

        {selectedLocation?.dms_price_enabled && (
          <FormHelper.Field.Input
            name="price"
            isPriceInput
            formFieldProps={{ width: 2, className: "mt-28" }}
            inputProps={{ labelPosition: "right", label: { basic: true, content: "€" }, disabled: isDisabled || followDMS }}
          />
        )}

        <Form.Field className="mt-28 label-dropdown-container">
          <ButtonGroup className="withdropdown" onClick={handleButtonGroupClick}>
            <Button className={`withdropdownButton ${pinTypeID === null ? "grey" : "black"}`} disabled={isDisabled}>
              <PinTypeIcon pinTypeId={pinTypeID || 0} />
            </Button>
            <Dropdown className="button icon" floating open={openDropdown} onOpen={toggleDropdown} onClose={toggleDropdown} disabled={isDisabled}>
              <DropdownMenu>
                <DropdownItem onClick={() => handlePinUpdate(null)} className="no-label" disabled={isDisabled}>
                  <Icon name="pin" />
                  {t("v8_no_label", "No Label")}
                </DropdownItem>
                {pinTypeOptions.map(option => (
                  <DropdownItem key={option.key} onClick={() => handlePinUpdate(option.value)} disabled={isDisabled} icon={option.icon} text={option.text} />
                ))}
              </DropdownMenu>
            </Dropdown>
          </ButtonGroup>
        </Form.Field>

        <Form.Field width={1} className="mt-28 user-approved-icon">
          <Button disabled={isDisabled || cannotToggleCustomerOK} onClick={toggleCustomerOk} className="wdLightGrey centerIcon greyBorder" type="button">
            <Icon className={`${customerOk ? "check circle green" : "remove circle red"}`} />
          </Button>
        </Form.Field>
      </Form.Group>

      <Form.Field width={16} className="mt-14">
        <label>{t("v8_description", "Description")}</label>
        <FormHelper.Field.TextArea
          name="description"
          textAreaProps={{ placeholder: t("v8_enter_description", "Enter description"), disabled: isDisabled || followDMS }}
        />
      </Form.Field>

      {intervention.internal_note && (
        <Form.Field width={16} className="mt-14">
          <label>{t("v8_dms_internal_note", "DMS Internal note")}</label>
          <div>{intervention.internal_note}</div>
        </Form.Field>
      )}

      <div className={intervention.is_local ? "flex-space-between" : "flex-end"}>
        {intervention.is_local && (
          <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.Interventions}>
            <Button color="red" type="button" onClick={handleDeleteLocalIntervention}>
              {t("v8_delete", "Delete")}
              <Icon className="trash" />
            </Button>
          </Can>
        )}

        <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Interventions}>
          <Button color="green" type="submit" disabled={!isDirty || !canUpdateInterventions}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </Can>
      </div>

      <CustomConfirm
        isLoading={isDeleteInterventionPending}
        isOpen={showDeleteInterventionModal}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        handleConfirm={handleDeleteLocalInterventionConfirm}
        handleCancel={handleCancelDeleteLocalIntervention}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
      />
    </>
  );
};
